/**
 * FP3-030A — Admin Access & Invites surface.
 *
 * Single admin-only page that consolidates the FP3-028 / FP3-030
 * invite + access-request workflow into a usable UI:
 *
 *   • Access requests list  — admins can approve a request (creates
 *     an invite + sends the invite email) or decline.
 *   • Invites list           — admins can see send status, send count,
 *     last-sent-at, and resend pending invites with one click.
 *   • Preview outbox panel   — preview/dev environments expose the
 *     captured email_outbox rows (template / to / subject /
 *     recorded_at / send mode / status). Hidden in production unless
 *     EMAIL_OUTBOX_ADMIN=1.
 *
 * Calm + compact by design. No student-specific information ever
 * surfaces here.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  EnvelopeSimple,
  CheckCircle,
  Warning,
  ArrowsClockwise,
  Eye,
  Clock,
  Plus,
  EyeSlash,
  CaretDown,
  CaretRight,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminShell from "./AdminShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import InviteUserModal from "@/components/admin/InviteUserModal";
// FP3-043 Phase C — Super-admin "show demo/test data" toggle.
import ShowHiddenDataToggle from "@/components/platform/ShowHiddenDataToggle";

// FP3-030B — Heuristic for system / test-generated invites.
// These are seeded by the regression suite and should not visually
// dominate the real admin tool. Kept hidden by default behind a
// "Show test/system invites" toggle.
const TEST_INVITE_EMAIL_PATTERNS = [
  /@example\.(com|org|net)$/i,
  /^cross-/i,
  /^foreign-/i,
  /pytest/i,
  /_test_/i,
  /^test[-_.]/i,
  /^_/,
];
function isTestInvite(inv) {
  const email = (inv.email || "").toLowerCase();
  return TEST_INVITE_EMAIL_PATTERNS.some((re) => re.test(email));
}

// FP3-030A — Status pill palette. Calm warm clay for failures,
// mint for success, slate for neutral states. Matches the rest of
// the Frame Practice product (no alarming red).
const STATUS_STYLES = {
  sent: { bg: "#E8F0EB", fg: "#1E3F33", label: "Sent" },
  preview_logged: { bg: "#EEF1F6", fg: "#3F4756", label: "Preview logged" },
  failed: { bg: "#F8EFEA", fg: "#7A3A2C", label: "Failed" },
  not_configured: { bg: "#F8EFEA", fg: "#7A3A2C", label: "Not configured" },
  not_sent: { bg: "#F5F4ED", fg: "#6B7280", label: "Email not sent" },
  disabled: { bg: "#F5F4ED", fg: "#6B7280", label: "Disabled" },
  accepted: { bg: "#E8F0EB", fg: "#1E3F33", label: "Accepted" },
  expired: { bg: "#F8EFEA", fg: "#7A3A2C", label: "Expired" },
  revoked: { bg: "#F8EFEA", fg: "#7A3A2C", label: "Revoked" },
  pending: { bg: "#EEF1F6", fg: "#3F4756", label: "Pending" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.not_sent;
  return (
    <span
      data-testid={`invite-status-${status}`}
      className="inline-flex items-center text-[10.5px] font-medium px-2 py-0.5 rounded-full"
      style={{ backgroundColor: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function formatTime(iso) {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    return dt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// FP3-048 — Display labels for the access-request `account_type`
// field. Kept lightweight so we don't drag a localization layer in.
function accountTypeLabel(value) {
  return (
    {
      school_team: "School / team",
      individual_provider: "Solo provider",
      other: "Other / not sure",
    }[(value || "").trim().toLowerCase()] || "—"
  );
}

// FP3-048 — Cell renderer for the Organization column. School-team
// requests show the typed organization name; solo-provider requests
// show a calm "Solo provider — organization on approval"; everything
// else falls back to "Not provided".
function organizationCellLabel(req) {
  const name = (req?.organization_or_school_name || "").trim();
  const type = (req?.account_type || "").trim().toLowerCase();
  if (name) return name;
  if (type === "individual_provider") {
    return "Solo provider — organization on approval";
  }
  return "Not provided";
}

export default function AdminAccess() {
  const { user } = useAuth();
  const isAdmin = ["admin", "org_admin", "super_admin"].includes(user?.permission_role || "");

  const [requests, setRequests] = useState([]);
  const [invites, setInvites] = useState([]);
  const [outbox, setOutbox] = useState(null); // null = not yet loaded, [] = empty
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState("");
  const [approvingId, setApprovingId] = useState("");
  const [configError, setConfigError] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [showTestInvites, setShowTestInvites] = useState(false);
  // FP3-048 — Approval modal state. When a request is selected the
  // modal walks the Super Admin through choosing/creating an org so
  // the resulting invite is always scoped.
  const [approveRequest, setApproveRequest] = useState(null);

  const load = async () => {
    setLoading(true);
    // FP3-053 — Pull super-admin context from URL and pass to both
    // lists so they narrow to the pinned org.
    const sp = new URLSearchParams(window.location.search);
    const asOrg = (sp.get("as_org") || "").trim();
    const params = asOrg ? { as_org: asOrg } : undefined;
    try {
      const [r, i] = await Promise.all([
        api.listAccessRequests(params),
        api.listInvites(params),
      ]);
      setRequests(r.data || []);
      setInvites(i.data || []);
    } catch (e) {
      toast.error("Could not load admin access data.");
    }
    // FP3-048 — Load orgs in the background for the approval modal.
    // Super admin gets every org; org admin gets just theirs.
    try {
      const o = await api.listOrganizations();
      setOrganizations(o.data || []);
    } catch {
      setOrganizations([]);
    }
    // Outbox is optional — production environments return 404 when
    // EMAIL_OUTBOX_ADMIN is unset. We swallow that quietly.
    try {
      const o = await api.listEmailOutbox();
      setOutbox(o.data || []);
    } catch {
      setOutbox([]);
    }
    setLoading(false);
  };

  // FP3-053 — Re-fetch when the sidebar context selector flips
  // `?as_org=` so the access requests + invites lists narrow to
  // the active super-admin context.
  const [searchParams] = useSearchParams();
  const asOrgUrl = searchParams.get("as_org") || "";
  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, asOrgUrl]);

  // FP3-048 — Replaced the legacy "directly POST approve" handler
  // with a two-step flow: clicking Approve opens the
  // ApproveAccessRequestModal where the Super Admin picks/creates
  // an organization. The modal calls onSubmit with a fully-scoped
  // InviteCreate payload so we never hit the raw 422 "Super Admin
  // invites must be scoped to an organization" toast in the UI.
  const openApproveModal = (req) => {
    setConfigError("");
    setApproveRequest(req);
  };

  const submitApproval = async (req, payload) => {
    setApprovingId(req.id);
    setConfigError("");
    try {
      await api.approveAccessRequest(req.id, payload);
      toast.success("Invite sent");
      setApproveRequest(null);
      await load();
    } catch (e) {
      if (e?.response?.status === 503) {
        setConfigError(
          e?.response?.data?.detail ||
            "Email is not configured for this environment."
        );
      } else {
        toast.error(e?.response?.data?.detail || "Could not send invite");
      }
    } finally {
      setApprovingId("");
    }
  };

  const declineRequest = async (req) => {
    try {
      await api.updateAccessRequest(req.id, { status: "declined" });
      toast.success("Request declined");
      await load();
    } catch {
      toast.error("Could not update request");
    }
  };

  const resendInvite = async (inv) => {
    setResendingId(inv.id);
    setConfigError("");
    try {
      const res = await api.resendInvite(inv.id);
      const after = res.data || {};
      toast.success(
        after.send_status === "preview_logged"
          ? "Invite resent (preview logged)"
          : "Invite resent"
      );
      await load();
    } catch (e) {
      if (e?.response?.status === 503) {
        setConfigError(
          e?.response?.data?.detail ||
            "Email is not configured for this environment."
        );
      } else {
        toast.error(e?.response?.data?.detail || "Could not resend invite");
      }
    } finally {
      setResendingId("");
    }
  };

  // FP3-030B — Hide system / test-generated invites by default so the
  // page reads as a real admin tool. Toggleable when needed.
  // Memos must run unconditionally to satisfy the rules-of-hooks.
  const testInvites = useMemo(() => invites.filter(isTestInvite), [invites]);
  const realInvites = useMemo(
    () => invites.filter((inv) => !isTestInvite(inv)),
    [invites]
  );

  // FP3-042D — Split visible invites into Pending vs Accepted/historical
  // so the operator can find the one they sent to themselves without
  // wading through hundreds of accepted demo rows. Pending invites get
  // top billing; accepted rows are tucked under a collapsible.
  const [inviteSearch, setInviteSearch] = useState("");
  const [showAccepted, setShowAccepted] = useState(false);

  const searchedInvites = useMemo(() => {
    const visible = showTestInvites ? invites : realInvites;
    const q = inviteSearch.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter((inv) => {
      const haystack = [
        inv.email || "",
        inv.first_name || "",
        inv.last_name || "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [invites, realInvites, showTestInvites, inviteSearch]);

  const pendingInvites = useMemo(
    () => searchedInvites.filter((inv) => inv.status === "pending"),
    [searchedInvites]
  );
  const acceptedInvites = useMemo(
    () => searchedInvites.filter((inv) => inv.status === "accepted"),
    [searchedInvites]
  );
  const otherInvites = useMemo(
    () =>
      searchedInvites.filter(
        (inv) => !["pending", "accepted"].includes(inv.status)
      ),
    [searchedInvites]
  );

  // FP3-042D — Legacy pending invites (no token_lookup_hash) require
  // a Resend to migrate onto the FP3-042C fast path. We surface them
  // with a small "Needs new link" badge so the admin knows clicking
  // Resend on these rows is a remediation step, not a duplicate.
  const legacyPendingCount = useMemo(
    () =>
      pendingInvites.filter(
        (inv) => !(inv.token_lookup_hash && String(inv.token_lookup_hash).length)
      ).length,
    [pendingInvites]
  );

  if (!isAdmin) {
    return (
      <AdminShell
        title="Access"
        subtitle="Admin-only."
      >
        <Card className="border-[#E5E3DB] rounded-2xl">
          <CardContent className="p-6 text-center text-[#6B7280] text-[13px]">
            You don't have permission to view this page.
          </CardContent>
        </Card>
      </AdminShell>
    );
  }

  const pendingRequests = requests.filter(
    (r) => !["invited", "declined"].includes(r.status)
  );
  const restRequests = requests.filter((r) =>
    ["invited", "declined"].includes(r.status)
  );

  const hiddenTestCount = testInvites.length;
  const renderInviteRow = (inv) => {
    const isPending = inv.status === "pending";
    const sendStatus = inv.send_status || "not_sent";
    const onFastPath = !!(inv.token_lookup_hash && String(inv.token_lookup_hash).length);
    return (
      <tr
        key={inv.id}
        data-testid={`invite-row-${inv.id}`}
        className="border-b border-[#F8F7F2] last:border-b-0"
      >
        <td className="px-4 py-2.5">
          <div className="text-[#1F2937] flex items-center gap-1.5 flex-wrap">
            {inv.first_name} {inv.last_name}
            {isTestInvite(inv) && (
              <span
                data-testid={`invite-system-tag-${inv.id}`}
                title="System / test-generated invite"
                className="text-[9px] uppercase tracking-[0.1em] font-semibold px-1.5 py-0.5 rounded bg-[#F5F4ED] text-[#9AA0A6]"
              >
                System
              </span>
            )}
            {isPending && !onFastPath && (
              <span
                data-testid={`invite-needs-refresh-${inv.id}`}
                title="This invite was created before the FP3-042C activation fix. Click Resend to issue a fresh link that uses the indexed fast path."
                className="text-[9px] uppercase tracking-[0.1em] font-semibold px-1.5 py-0.5 rounded bg-[#FDF3D8] text-[#7A6A2C]"
              >
                Needs new link
              </span>
            )}
          </div>
          <div className="text-[#6B7280] font-mono text-[11.5px]">
            {inv.email}
          </div>
        </td>
        <td className="px-4 py-2.5 text-[#6B7280]">
          {(inv.intended_role || "provider").replace(/_/g, " ")}
          {inv.provider_specialty
            ? ` · ${inv.provider_specialty}`
            : ""}
          <div className="text-[11px] text-[#9AA0A6] mt-0.5">
            {(inv.access_type || "").toUpperCase()}
          </div>
        </td>
        <td className="px-4 py-2.5">
          <StatusPill status={inv.status || "pending"} />
        </td>
        <td className="px-4 py-2.5">
          <div className="flex flex-col gap-0.5">
            <StatusPill status={sendStatus} />
            <span
              data-testid={`invite-send-count-${inv.id}`}
              className="text-[10.5px] text-[#9AA0A6]"
            >
              {inv.send_count || 0}× sent
            </span>
          </div>
        </td>
        <td className="px-4 py-2.5 text-[#6B7280]">
          {formatTime(inv.last_sent_at)}
          {inv.last_send_error && (
            <div
              data-testid={`invite-error-${inv.id}`}
              className="text-[10.5px] text-[#7A3A2C] mt-0.5 max-w-[180px] truncate"
              title={inv.last_send_error}
            >
              {inv.last_send_error}
            </div>
          )}
        </td>
        <td className="px-4 py-2.5 text-right">
          {isPending ? (
            <Button
              data-testid={`invite-resend-${inv.id}`}
              onClick={() => resendInvite(inv)}
              disabled={resendingId === inv.id}
              variant="outline"
              className="h-7 px-2.5 text-[11.5px] border-[#1E3F33]/30 text-[#1E3F33] hover:bg-[#E8F0EB]"
            >
              {resendingId === inv.id ? (
                <>
                  <ArrowsClockwise
                    size={11}
                    className="mr-1 animate-spin"
                  />
                  Resending…
                </>
              ) : (
                <>
                  <EnvelopeSimple size={11} className="mr-1" />
                  Resend
                </>
              )}
            </Button>
          ) : (
            <span
              data-testid={`invite-no-action-${inv.id}`}
              className="text-[11px] text-[#9AA0A6]"
            >
              {inv.status === "accepted" ? "Accepted" : "—"}
            </span>
          )}
        </td>
      </tr>
    );
  };

  const inviteTableHeader = (
    <thead>
      <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
        <th className="text-left px-4 py-2.5">Recipient</th>
        <th className="text-left px-4 py-2.5">Role · Access</th>
        <th className="text-left px-4 py-2.5">Invite status</th>
        <th className="text-left px-4 py-2.5">Email</th>
        <th className="text-left px-4 py-2.5">Sent</th>
        <th className="text-right px-4 py-2.5">Action</th>
      </tr>
    </thead>
  );

  return (
    <AdminShell
      title="Access · Invites & Requests"
      subtitle="Approve access requests and manage invite emails."
      actions={
        <div className="flex items-center gap-2">
          <ShowHiddenDataToggle
            testid="admin-access-show-hidden-toggle"
            onChange={() => load()}
          />
          <Button
            data-testid="admin-invite-user-btn"
            onClick={() => setInviteModalOpen(true)}
            className="h-9 px-3.5 text-[12.5px] bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            <Plus size={13} weight="bold" className="mr-1.5" />
            Invite user
          </Button>
        </div>
      }
    >
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onCreated={() => load()}
      />
      {configError && (
        <div
          data-testid="admin-access-config-error"
          className="mb-5 rounded-md border border-[#E8D6CD] bg-[#F8EFEA] text-[#7A3A2C] text-[12.5px] px-3 py-2"
        >
          {configError}
        </div>
      )}

      {/* FP3-042D — Pending Invites section moved to top so operators
          can find the invite they just sent without scrolling past
          dozens of pending access requests. */}
      <section data-testid="admin-pending-invites-section" className="mb-8">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h2 className="text-[13px] font-semibold text-[#1F2937] heading-font uppercase tracking-[0.08em] flex items-center gap-2">
            Pending invites
            <span
              data-testid="admin-pending-invites-count"
              className="text-[11px] text-[#6B7280] font-medium"
            >
              ({pendingInvites.length})
            </span>
            {legacyPendingCount > 0 && (
              <span
                data-testid="admin-legacy-pending-count"
                title="Pending invites that pre-date the FP3-042C activation fix. Click Resend on each to rotate to a fresh link that uses the indexed fast path."
                className="text-[10px] uppercase tracking-[0.1em] font-semibold px-1.5 py-0.5 rounded bg-[#FDF3D8] text-[#7A6A2C]"
              >
                {legacyPendingCount} need new link
              </span>
            )}
          </h2>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <MagnifyingGlass
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9AA0A6]"
              />
              <input
                data-testid="admin-invite-search"
                value={inviteSearch}
                onChange={(e) => setInviteSearch(e.target.value)}
                placeholder="Filter by name or email…"
                className="pl-7 pr-3 h-7 w-[220px] text-[11.5px] rounded-md border border-[#E5E3DB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/20"
              />
            </div>
            {hiddenTestCount > 0 && (
              <button
                type="button"
                data-testid="admin-toggle-test-invites"
                onClick={() => setShowTestInvites((v) => !v)}
                className="inline-flex items-center gap-1 text-[11px] text-[#3F4756] hover:text-[#1F2937] underline-offset-2 hover:underline"
              >
                {showTestInvites ? (
                  <>
                    <EyeSlash size={11} weight="duotone" />
                    Hide test/system ({hiddenTestCount})
                  </>
                ) : (
                  <>
                    <Eye size={11} weight="duotone" />
                    Show test/system ({hiddenTestCount})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        <Card className="border-[#E5E3DB] rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-center text-[#6B7280] text-[13px]">
                Loading…
              </div>
            ) : pendingInvites.length === 0 ? (
              <div
                data-testid="admin-no-pending-invites"
                className="p-6 text-center text-[#6B7280] text-[13px]"
              >
                {inviteSearch
                  ? "No pending invites match your filter."
                  : "No pending invites. Click \"Invite user\" to send one."}
              </div>
            ) : (
              <table className="w-full text-[12.5px]">
                {inviteTableHeader}
                <tbody>{pendingInvites.map(renderInviteRow)}</tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Access Requests */}
      <section
        data-testid="admin-access-requests-section"
        className="mb-8"
      >
        <h2 className="text-[13px] font-semibold text-[#1F2937] heading-font uppercase tracking-[0.08em] mb-3">
          Pending access requests
          {pendingRequests.length > 0 && (
            <span className="ml-2 text-[11px] text-[#6B7280] font-medium">
              ({pendingRequests.length})
            </span>
          )}
        </h2>
        <Card className="border-[#E5E3DB] rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-center text-[#6B7280] text-[13px]">
                Loading…
              </div>
            ) : pendingRequests.length === 0 ? (
              <div
                data-testid="admin-no-pending-requests"
                className="p-6 text-center text-[#6B7280] text-[13px]"
              >
                No pending access requests.
              </div>
            ) : (
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                    <th className="text-left px-4 py-2.5">Requester</th>
                    <th className="text-left px-4 py-2.5">Email</th>
                    <th className="text-left px-4 py-2.5">Request type</th>
                    <th className="text-left px-4 py-2.5">Role · Specialty</th>
                    <th className="text-left px-4 py-2.5">Organization</th>
                    <th className="text-left px-4 py-2.5">Submitted</th>
                    <th className="text-right px-4 py-2.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRequests.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`access-request-row-${r.id}`}
                      className="border-b border-[#F8F7F2] last:border-b-0"
                    >
                      <td className="px-4 py-2.5 text-[#1F2937]">
                        {r.requester_first_name} {r.requester_last_name}
                      </td>
                      <td className="px-4 py-2.5 text-[#1F2937] font-mono text-[12px]">
                        {r.email}
                      </td>
                      <td
                        data-testid={`access-request-type-${r.id}`}
                        className="px-4 py-2.5 text-[#6B7280]"
                      >
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {accountTypeLabel(r.account_type)}
                          {/* FP3-052 — Assistant add-on pill. Quick
                              visual cue on the row before the
                              Super Admin opens the approval modal. */}
                          {r.wants_assistant_addons && (
                            <span
                              data-testid={`access-request-assistants-pill-${r.id}`}
                              title={`Assistants requested: ${r.requested_assistant_count || "—"} · ${(r.requested_assistant_types || []).join(", ") || "—"}`}
                              className="inline-flex items-center text-[9.5px] font-semibold uppercase tracking-[0.08em] rounded-full px-1.5 py-0.5 bg-[#FBF3E4] text-[#9C6B2E] leading-none whitespace-nowrap"
                            >
                              +{r.requested_assistant_count || ""} assistants
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-[#6B7280]">
                        {(r.role_interest || "").replace(/_/g, " ")}
                        {r.provider_specialty_interest
                          ? ` · ${r.provider_specialty_interest}`
                          : ""}
                      </td>
                      <td
                        data-testid={`access-request-org-${r.id}`}
                        className="px-4 py-2.5 text-[#6B7280]"
                      >
                        {organizationCellLabel(r)}
                      </td>
                      <td className="px-4 py-2.5 text-[#6B7280]">
                        {formatTime(r.created_at)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            data-testid={`access-decline-${r.id}`}
                            onClick={() => declineRequest(r)}
                            variant="outline"
                            className="h-7 px-2.5 text-[11.5px] border-[#E5E3DB] text-[#6B7280] hover:bg-[#F8F7F2]"
                          >
                            Decline
                          </Button>
                          <Button
                            data-testid={`access-approve-${r.id}`}
                            onClick={() => openApproveModal(r)}
                            disabled={approvingId === r.id}
                            className="h-7 px-3 text-[11.5px] bg-[#1E3F33] hover:bg-[#173127] text-white"
                          >
                            {approvingId === r.id ? "Sending…" : "Approve + Send invite"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </section>

      {/* FP3-042D — Accepted invites tucked under a collapsible so the
          (often very long) historical list doesn't visually drown the
          actionable Pending sections. */}
      <section data-testid="admin-accepted-invites-section" className="mb-8">
        <button
          type="button"
          data-testid="admin-toggle-accepted-invites"
          onClick={() => setShowAccepted((v) => !v)}
          className="w-full flex items-center justify-between mb-3 text-left"
        >
          <h2 className="text-[13px] font-semibold text-[#1F2937] heading-font uppercase tracking-[0.08em] flex items-center gap-2">
            {showAccepted ? <CaretDown size={12} /> : <CaretRight size={12} />}
            Accepted invites
            <span className="text-[11px] text-[#6B7280] font-medium">
              ({acceptedInvites.length})
            </span>
          </h2>
          <span className="text-[11px] text-[#6B7280]">
            {showAccepted ? "Hide" : "Show"}
          </span>
        </button>
        {showAccepted && (
          <Card className="border-[#E5E3DB] rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              {acceptedInvites.length === 0 ? (
                <div className="p-6 text-center text-[#6B7280] text-[13px]">
                  No accepted invites match your filter.
                </div>
              ) : (
                <table className="w-full text-[12.5px]">
                  {inviteTableHeader}
                  <tbody>{acceptedInvites.slice(0, 200).map(renderInviteRow)}</tbody>
                </table>
              )}
              {acceptedInvites.length > 200 && (
                <div className="px-4 py-2.5 text-[11px] text-[#9AA0A6] border-t border-[#F1F2F4]">
                  Showing the 200 most recent accepted invites.
                  Use the filter above to find a specific row.
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </section>

      {/* FP3-042D — Other invite statuses (expired/revoked) kept
          available for visibility without crowding the active sections. */}
      {otherInvites.length > 0 && (
        <section data-testid="admin-other-invites-section" className="mb-8">
          <h2 className="text-[13px] font-semibold text-[#1F2937] heading-font uppercase tracking-[0.08em] mb-3">
            Other invites
            <span className="ml-2 text-[11px] text-[#6B7280] font-medium">
              ({otherInvites.length})
            </span>
          </h2>
          <Card className="border-[#E5E3DB] rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <table className="w-full text-[12.5px]">
                {inviteTableHeader}
                <tbody>{otherInvites.map(renderInviteRow)}</tbody>
              </table>
            </CardContent>
          </Card>
        </section>
      )}

      {/* History — declined/invited request rows */}
      {restRequests.length > 0 && (
        <section data-testid="admin-access-history-section" className="mb-8">
          <h2 className="text-[13px] font-semibold text-[#1F2937] heading-font uppercase tracking-[0.08em] mb-3">
            History
          </h2>
          <Card className="border-[#E5E3DB] rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                    <th className="text-left px-4 py-2.5">Requester</th>
                    <th className="text-left px-4 py-2.5">Email</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-left px-4 py-2.5">When</th>
                  </tr>
                </thead>
                <tbody>
                  {restRequests.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b border-[#F8F7F2] last:border-b-0"
                    >
                      <td className="px-4 py-2.5 text-[#1F2937]">
                        {r.requester_first_name} {r.requester_last_name}
                      </td>
                      <td className="px-4 py-2.5 text-[#1F2937] font-mono text-[12px]">
                        {r.email}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-4 py-2.5 text-[#6B7280]">
                        {formatTime(r.reviewed_at || r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Preview outbox (preview/dev only) */}
      {Array.isArray(outbox) && outbox.length > 0 && (
        <section data-testid="admin-outbox-section" className="mb-8">
          <h2 className="text-[13px] font-semibold text-[#1F2937] heading-font uppercase tracking-[0.08em] mb-3 flex items-center gap-2">
            <Eye size={14} weight="duotone" className="text-[#3F4756]" />
            Preview outbox
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#9AA0A6] font-semibold ml-1">
              · preview / dev
            </span>
          </h2>
          <Card className="border-[#E5E3DB] rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                    <th className="text-left px-4 py-2.5">Template</th>
                    <th className="text-left px-4 py-2.5">Recipient</th>
                    <th className="text-left px-4 py-2.5">Subject</th>
                    <th className="text-left px-4 py-2.5">Mode</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-left px-4 py-2.5">Recorded</th>
                  </tr>
                </thead>
                <tbody>
                  {outbox.slice(0, 25).map((row) => (
                    <tr
                      key={row.id}
                      data-testid={`outbox-row-${row.id}`}
                      className="border-b border-[#F8F7F2] last:border-b-0"
                    >
                      <td className="px-4 py-2.5 text-[#1F2937]">
                        {row.template}
                      </td>
                      <td className="px-4 py-2.5 text-[#1F2937] font-mono text-[11.5px]">
                        {row.to}
                      </td>
                      <td className="px-4 py-2.5 text-[#6B7280]">
                        {row.subject}
                      </td>
                      <td className="px-4 py-2.5 text-[#6B7280] uppercase text-[10.5px] tracking-[0.1em]">
                        {row.send_mode}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusPill status={row.status || "preview_logged"} />
                      </td>
                      <td className="px-4 py-2.5 text-[#6B7280]">
                        {formatTime(row.recorded_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </section>
      )}

      <p className="text-center text-[10.5px] text-[#9AA0A6]">
        Frame Practice is invite-only. Invites are single-use, expire in 14 days,
        and rotate their token on every resend.
      </p>

      {/* FP3-048 — Approval modal. Mounted at the page root so the
          backdrop covers the full admin shell. */}
      <ApproveAccessRequestModal
        open={!!approveRequest}
        request={approveRequest}
        organizations={organizations}
        isSuperAdmin={user?.permission_role === "super_admin"}
        currentOrgId={user?.organization_id || ""}
        submitting={approvingId === (approveRequest?.id || "__none__")}
        onCancel={() => setApproveRequest(null)}
        onSubmit={(payload) => submitApproval(approveRequest, payload)}
      />
    </AdminShell>
  );
}

// ── FP3-048 — Approve Access Request modal ─────────────────────────

function ApproveAccessRequestModal({
  open,
  request,
  organizations,
  isSuperAdmin,
  currentOrgId,
  submitting,
  onCancel,
  onSubmit,
}) {
  const requestedOrgName = (request?.organization_or_school_name || "").trim();
  const accountType = (request?.account_type || "").trim().toLowerCase();
  const requesterFirst = (request?.requester_first_name || "").trim();
  const requesterLast = (request?.requester_last_name || "").trim();
  const requesterFullName = `${requesterFirst} ${requesterLast}`.trim();

  // Mode: "existing" | "create"
  // - existing → user picks one of the org rows we already have
  // - create   → user types/edits a name (and account_type for solo)
  //
  // Defaults depend on whether the request landed with an org name:
  //   • School/team request WITH org name → default to "create" (a
  //     brand new org for this pilot) so the typical path doesn't
  //     accidentally merge two different schools that happen to
  //     share a name string. The Super Admin can still flip to
  //     "existing" if they want.
  //   • Solo provider WITHOUT org name → default to "create" with
  //     suggested name "Solo Provider — First Last".
  //   • Anything else → default to "existing" if there are orgs to
  //     pick from, otherwise "create".
  const [mode, setMode] = useState("create");
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [newName, setNewName] = useState("");
  const [newAccountType, setNewAccountType] = useState("school_team");

  // Reset modal state any time a new request is opened.
  useEffect(() => {
    if (!request) return;
    if (accountType === "individual_provider") {
      setMode("create");
      setNewName(
        requesterFullName
          ? `Solo Provider — ${requesterFullName}`
          : "Solo Provider"
      );
      setNewAccountType("individual_provider");
      setSelectedOrgId("");
    } else if (accountType === "school_team") {
      setMode("create");
      setNewName(requestedOrgName);
      setNewAccountType("school_team");
      setSelectedOrgId("");
    } else {
      // "other" or unknown — let the Super Admin choose.
      const defaultMode = organizations.length ? "existing" : "create";
      setMode(defaultMode);
      setNewName(requestedOrgName);
      setNewAccountType("school_team");
      setSelectedOrgId(organizations[0]?.id || "");
    }
    // If the admin isn't super_admin, they can only invite into
    // their own org — preselect it and force the "existing" mode.
    if (!isSuperAdmin && currentOrgId) {
      setMode("existing");
      setSelectedOrgId(currentOrgId);
    }
  }, [request, accountType, requesterFullName, requestedOrgName, organizations, isSuperAdmin, currentOrgId]);

  if (!open || !request) return null;

  const matchingExistingOrg = requestedOrgName
    ? organizations.find(
        (o) => (o.name || "").trim().toLowerCase() === requestedOrgName.toLowerCase()
      )
    : null;

  const canCreate = isSuperAdmin;
  const trimmedNew = newName.trim();
  const submitDisabled =
    submitting ||
    (mode === "existing" && !selectedOrgId) ||
    (mode === "create" && (!canCreate || !trimmedNew));

  const handleSubmit = () => {
    const intendedRole =
      (request.role_interest || "").toLowerCase() === "administrator"
        ? "admin"
        : "provider";
    const base = {
      email: request.email,
      first_name: requesterFirst,
      last_name: requesterLast,
      intended_role: intendedRole,
      provider_specialty: request.provider_specialty_interest || "",
      access_type: "trial",
    };
    if (mode === "existing") {
      onSubmit({ ...base, organization_id: selectedOrgId });
    } else {
      onSubmit({
        ...base,
        create_organization: true,
        new_organization_name: trimmedNew,
        new_organization_account_type: newAccountType,
      });
    }
  };

  return (
    <div
      data-testid="approve-access-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F2937]/40 backdrop-blur-sm px-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl border border-[#E5E3DB] shadow-[0_18px_60px_rgba(31,41,55,0.18)] w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-3">
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold">
            Approve access request
          </div>
          <div className="mt-1 text-[18px] font-semibold text-[#1F2937] heading-font leading-tight">
            {requesterFullName || request.email}
          </div>
          <div className="mt-1 text-[12px] text-[#6B7280]">
            {request.email} · {accountTypeLabel(accountType)}
          </div>
        </div>

        {/* FP3-052 — Assistant add-on request summary (solo provider only).
            Shown read-only so the Super Admin can plan capacity /
            supervision before approving. No automatic assistant
            invite is sent in this pass. */}
        {request.wants_assistant_addons && (
          <div
            data-testid="approve-assistant-summary"
            className="mx-6 mt-3 rounded-md border border-[#E0CFA8] bg-[#FBF3E4] p-3 text-[12px] text-[#7A3A2C]"
          >
            <div className="font-semibold text-[#7A3A2C] mb-1">
              Assistant add-ons requested
            </div>
            <div className="leading-relaxed">
              <span className="font-medium">Count:</span>{" "}
              {request.requested_assistant_count || "—"}
              {" · "}
              <span className="font-medium">Types:</span>{" "}
              {(request.requested_assistant_types || []).join(", ") || "—"}
            </div>
            {(request.assistant_request_note || "").trim() && (
              <div className="mt-1.5 text-[11.5px] text-[#9C6B2E]">
                "{request.assistant_request_note}"
              </div>
            )}
            <div className="mt-2 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#9C6B2E]">
              Invite assistants after primary provider setup.
            </div>
          </div>
        )}

        <div className="px-6 pb-2 pt-3 border-t border-[#F1F2F4]">
          <div className="text-[11px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold mb-2">
            Organization scope<span className="text-[#9C6B2E] ml-0.5">*</span>
          </div>
          {requestedOrgName && (
            <div className="text-[12px] text-[#1F2937] bg-[#F8F7F2] border border-[#E5E3DB] rounded-md px-3 py-2 mb-3">
              Requester typed: <strong>{requestedOrgName}</strong>
              {matchingExistingOrg && (
                <span className="ml-2 text-[10.5px] uppercase tracking-[0.1em] text-[#1E3F33] font-semibold">
                  · matches existing org
                </span>
              )}
            </div>
          )}
          {!requestedOrgName && accountType === "individual_provider" && (
            <div
              data-testid="approve-solo-helper"
              className="text-[11.5px] text-[#6B7280] bg-[#F8F7F2] border border-[#E5E3DB] rounded-md px-3 py-2 mb-3 leading-relaxed"
            >
              No organization on request. We'll create a solo workspace using
              the requester's name. You can edit it before approving.
            </div>
          )}
          {!requestedOrgName &&
            accountType !== "individual_provider" &&
            mode === "create" && (
              <div
                data-testid="approve-needs-org-helper"
                className="text-[11.5px] text-[#9C6B2E] bg-[#FBF3E4] border border-[#E5C390] rounded-md px-3 py-2 mb-3 leading-relaxed"
              >
                Request didn't include an organization. Choose one below or
                create a new organization before sending the invite.
              </div>
            )}

          {/* Mode toggle — only meaningful for super_admin */}
          {canCreate && organizations.length > 0 && (
            <div
              data-testid="approve-mode-toggle"
              className="inline-flex p-0.5 bg-[#F1F2F4] rounded-md mb-3 text-[11.5px]"
            >
              <button
                type="button"
                data-testid="approve-mode-existing"
                onClick={() => setMode("existing")}
                className={`px-2.5 py-1 rounded ${
                  mode === "existing"
                    ? "bg-white text-[#1E3F33] font-semibold shadow-sm"
                    : "text-[#6B7280]"
                }`}
              >
                Use existing
              </button>
              <button
                type="button"
                data-testid="approve-mode-create"
                onClick={() => setMode("create")}
                className={`px-2.5 py-1 rounded ${
                  mode === "create"
                    ? "bg-white text-[#1E3F33] font-semibold shadow-sm"
                    : "text-[#6B7280]"
                }`}
              >
                Create new
              </button>
            </div>
          )}

          {mode === "existing" ? (
            <select
              data-testid="approve-existing-org-select"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
            >
              <option value="">Select an organization…</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                  {o.account_type ? ` · ${accountTypeShortLabel(o.account_type)}` : ""}
                </option>
              ))}
            </select>
          ) : (
            <div className="space-y-2.5">
              <div>
                <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                  New organization name
                </Label>
                <Input
                  data-testid="approve-new-org-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="mt-1.5 h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </div>
              <div>
                <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                  Organization type
                </Label>
                <select
                  data-testid="approve-new-org-account-type"
                  value={newAccountType}
                  onChange={(e) => setNewAccountType(e.target.value)}
                  className="mt-1.5 block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
                >
                  <option value="school_team">School / team</option>
                  <option value="district">District</option>
                  <option value="individual_provider">Solo provider</option>
                </select>
              </div>
              {!canCreate && (
                <p className="text-[11px] text-[#9C6B2E] leading-relaxed">
                  Only Super Admin can mint a new organization. Switch to
                  "Use existing" or hand this off to a Super Admin.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-[#FBFAF6] border-t border-[#EDEAE0] flex items-center justify-end gap-2">
          <Button
            data-testid="approve-modal-cancel"
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
            className="h-9 px-3 text-[12.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F8F7F2]"
          >
            Cancel
          </Button>
          <Button
            data-testid="approve-modal-submit"
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="h-9 px-4 text-[12.5px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/40 text-white"
          >
            {submitting ? "Sending…" : "Approve + Send invite"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function accountTypeShortLabel(value) {
  return (
    {
      school_team: "School / team",
      district: "District",
      individual_provider: "Solo",
    }[(value || "").trim().toLowerCase()] || value
  );
}
